/**
 * Designer-side object ID rules.
 *
 * A template object keeps the ID assigned in the Designer.  This module is
 * deliberately independent from the firmware/runtime: it only answers which
 * Designer objects can coexist on a runtime page and allocates an ID that is
 * safe for that scope.
 */
import { attachPageLogicActions } from '../logic/pageLogic.js';

export const FIRST_DESIGNER_OBJECT_ID = 1;
export const LAST_DESIGNER_OBJECT_ID = 254;
export const RESERVED_DESIGNER_OBJECT_IDS = new Set([200]);

export function getTemplateObjectRecords(templatePage, templateObjectId = '', { includePage = true } = {}) {
    const lines = [];
    templatePage?.export?.(templatePage, lines, false);
    // Template records are runtime records: include compiled event/action nodes.
    attachPageLogicActions(templatePage, lines);
    const records = lines
        .filter(line => line.obj !== 'template_page')
        .map(line => {
            const record = includePage
                ? { page: Number(templatePage?.targetPage ?? 0), ...line }
                : { ...line };
            if (!includePage) delete record.page;
            return record;
        });

    if (templateObjectId === '' || templateObjectId === null || templateObjectId === undefined) {
        return records;
    }

    const rootId = Number(templateObjectId);
    const childrenOf = new Map();
    for (const line of records) {
        if (line.parentid === undefined) continue;
        const children = childrenOf.get(Number(line.parentid)) ?? [];
        children.push(Number(line.id));
        childrenOf.set(Number(line.parentid), children);
    }

    const included = new Set();
    const queue = [rootId];
    while (queue.length) {
        const id = queue.shift();
        if (included.has(id)) continue;
        included.add(id);
        queue.push(...(childrenOf.get(id) ?? []));
    }

    return records
        .filter(record => included.has(Number(record.id)))
        .map(record => {
            if (Number(record.id) !== rootId) return record;
            const detached = { ...record };
            delete detached.parentid;
            return detached;
        });
}

/** Return all non-page objects in the layer with their owning page/template. */
export function collectDesignerObjects(layer) {
    const result = [];
    const visit = (node, owner) => {
        if (!node) return;
        const isPage = node.obj === 'page' || node.obj === 'template_page';
        const nextOwner = isPage ? node : owner;
        if (!isPage && node.haspid !== undefined && node.haspid !== null) {
            result.push({ object: node, owner: nextOwner });
        }
        (node.getChildren?.() ?? []).forEach(child => visit(child, nextOwner));
    };
    (layer?.getChildren?.() ?? []).forEach(page => visit(page, null));
    return result;
}

/**
 * Find the regular pages on which a template may be instantiated.  Explicit
 * popup metadata is used when available; an unknown usage is handled
 * conservatively by considering every regular page.
 */
export function getTemplateRuntimePageIds(template, layer, context = {}) {
    const pages = context.pages ?? layer?.getChildren?.() ?? [];
    const regularPages = pages.filter(page => page?.obj === 'page');
    const templateId = Number(template?.haspid);
    const pageIds = new Set();
    let hasExplicitInvocation = false;

    if (Number.isInteger(Number(template?.targetPage))) {
        pageIds.add(Number(template.targetPage));
    }

    const scan = (node, ownerPage) => {
        if (!node) return;
        const cfg = node.hass_config ?? {};
        const scanValue = value => {
            if (!value || typeof value !== 'object') return;
            if (Number(value._popupTemplateId) === templateId && Number.isInteger(Number(value._popupTargetPageId))) {
                pageIds.add(Number(value._popupTargetPageId));
                hasExplicitInvocation = true;
            }
            if (Number(value.templateId) === templateId && ownerPage?.obj === 'page') {
                pageIds.add(Number(ownerPage.haspid));
                hasExplicitInvocation = true;
            }
            Object.values(value).forEach(scanValue);
        };
        scanValue(cfg);
        (node.getChildren?.() ?? []).forEach(child => scan(child, ownerPage));
    };
    regularPages.forEach(page => scan(page, page));

    // targetPage is legacy template metadata, not a complete instance map.
    // Without explicit invocation metadata, an unseen page can still open the
    // template, so be conservative and reserve the ID on every page.
    if (!hasExplicitInvocation) {
        pageIds.clear();
        regularPages.forEach(page => pageIds.add(Number(page.haspid)));
    }
    if (pageIds.size === 0 && Number.isInteger(Number(template?.targetPage))) {
        pageIds.add(Number(template.targetPage));
    }
    if (pageIds.size === 0) pageIds.add('*');
    return pageIds;
}

export function getObjectRuntimePageIds(object, owner, layer, context = {}) {
    if (owner?.obj === 'template_page') {
        return getTemplateRuntimePageIds(owner, layer, context);
    }
    if (owner?.obj === 'page' && Number.isInteger(Number(owner.haspid))) {
        return new Set([Number(owner.haspid)]);
    }
    return new Set();
}

function isValidObjectId(id) {
    return Number.isInteger(Number(id)) &&
        Number(id) >= FIRST_DESIGNER_OBJECT_ID &&
        Number(id) <= LAST_DESIGNER_OBJECT_ID;
}

function scopesOverlap(left, right) {
    if (left.has('*') || right.has('*')) return true;
    for (const pageId of left) if (right.has(pageId)) return true;
    return false;
}

function describe(entry) {
    const owner = entry.owner;
    const ownerLabel = owner?.obj === 'template_page'
        ? `template T${owner.haspid}`
        : `page P${owner?.haspid}`;
    return `${ownerLabel} object ${entry.object.haspid}`;
}

/** Validate every object ID in the Designer against all possible peers. */
export function validateDesignerObjectIds(layer, context = {}) {
    const entries = collectDesignerObjects(layer);
    const errors = [];
    const seen = new Set();

    for (const entry of entries) {
        const id = Number(entry.object.haspid);
        const key = `${entry.owner?.obj}:${entry.owner?.haspid}:${id}`;
        if (!isValidObjectId(id)) {
            errors.push(`${describe(entry)} has invalid object ID ${entry.object.haspid}; valid IDs are 1–254`);
        }
        if (RESERVED_DESIGNER_OBJECT_IDS.has(id)) {
            errors.push(`${describe(entry)} uses reserved object ID ${id}`);
        }
        const scopes = getObjectRuntimePageIds(entry.object, entry.owner, layer, context);
        for (const peer of entries) {
            if (peer === entry || Number(peer.object.haspid) !== id) continue;
            const peerScopes = getObjectRuntimePageIds(peer.object, peer.owner, layer, context);
            if (!scopesOverlap(scopes, peerScopes)) continue;
            const pair = [key, `${peer.owner?.obj}:${peer.owner?.haspid}:${id}`].sort().join('|');
            if (seen.has(pair)) continue;
            seen.add(pair);
            errors.push(`Object ID ${id} is used by ${describe(entry)} and ${describe(peer)} on the same possible runtime page`);
        }
    }
    return [...new Set(errors)];
}

/** Validate a proposed ID without mutating the object. */
export function validateDesignerObjectId(object, id, layer, context = {}) {
    const numericId = Number(id);
    if (!isValidObjectId(numericId)) {
        return { valid: false, error: `Object ID must be an integer from 1 to 254` };
    }
    if (RESERVED_DESIGNER_OBJECT_IDS.has(numericId)) {
        return { valid: false, error: `Object ID ${numericId} is reserved` };
    }

    const entries = collectDesignerObjects(layer);
    const entry = entries.find(item => item.object === object);
    if (!entry) return { valid: true, error: '' };
    const scopes = getObjectRuntimePageIds(object, entry.owner, layer, context);
    const collision = entries.find(peer =>
        peer.object !== object &&
        Number(peer.object.haspid) === numericId &&
        scopesOverlap(scopes, getObjectRuntimePageIds(peer.object, peer.owner, layer, context))
    );
    if (collision) {
        return { valid: false, error: `Object ID ${numericId} conflicts with ${describe(collision)} on the same possible runtime page` };
    }
    return { valid: true, error: '' };
}

/** Allocate the first free actual Designer ID for a page/template. */
export function allocateDesignerObjectId(owner, layer, start = FIRST_DESIGNER_OBJECT_ID, context = {}) {
    const entries = collectDesignerObjects(layer);
    const scopes = getObjectRuntimePageIds(null, owner, layer, context);
    const reservedIds = context.reservedIds ?? new Set();
    for (let id = Math.max(FIRST_DESIGNER_OBJECT_ID, Number(start) || FIRST_DESIGNER_OBJECT_ID);
        id <= LAST_DESIGNER_OBJECT_ID; id++) {
        if (RESERVED_DESIGNER_OBJECT_IDS.has(id)) continue;
        if (reservedIds.has(id)) continue;
        const occupied = entries.some(entry =>
            Number(entry.object.haspid) === id &&
            scopesOverlap(scopes, getObjectRuntimePageIds(entry.object, entry.owner, layer, context))
        );
        if (!occupied) return id;
    }
    throw new RangeError('No free Designer object IDs remain (valid range: 1–254)');
}
